import React, {useRef, ReactNode} from 'react'
import {
  MantineColor,
  useSafeMantineTheme,
  MantineSize,
  MantineRadius,
  StylesApiProps,
  Factory,
  SliderCssVariables,
  ElementProps,
  BoxProps,
  TransitionOverride,
  createVarsResolver,
  getSize,
  getThemeColor,
  getRadius,
  rem,
  useStyles,
  factory,
  useProps,
  MantineComponent
} from '@mantine/core'
import {Thumb, ThumbProps} from './Thumb'
import {Track} from './Track'
import {SliderRoot} from './SliderRoot'
import {SliderProvider, SliderStylesNames, getPosition, getPrecision} from './slider'
import {mapEntries} from 'shared/collection'
import {RefProps} from 'components'
import {useDrag} from 'components/hooks'
import {match} from 'ts-pattern'
import classes from './Slider.module.css'
import {useMergedRef} from '@mantine/hooks'

export type ThumbCfg = {
  children?: (value: number) => ReactNode
  shouldClamp?: boolean
  showLabel?: boolean
  tip?: ReactNode
  hideOutOfRange?: boolean
} & Partial<Pick<ThumbProps, 'shape' | 'onFocus' | 'onBlur' | 'labelAlwaysOn' | 'label' | 'thumbSize' | 'className'>>

// type ThumbState = { value: number, focused?: boolean, hovered?:boolean}

export interface SliderProps<K extends string = string>
  extends BoxProps,
    StylesApiProps<SliderFactory>,
    ElementProps<'div', 'onChange'> {
  /** Current value for controlled slider */
  value: Record<K, number>
  onChange(props: {name: string; value: number; mode: 'arrows' | 'drag'}): void
  /** Called when user stops dragging slider or changes value with arrows */
  onDragEnd?(): void
  thumbs: Record<K, ThumbCfg>
  /** Key of `theme.colors` or any valid CSS color, controls color of track and thumb, `theme.primaryColor` by default */
  color?: MantineColor

  /** Key of `theme.radius` or any valid CSS value to set `border-radius`, numbers are converted to rem, `'xl'` by default */
  radius?: MantineRadius

  /** Controls size of the track, `'md'` by default */
  size?: MantineSize | (string & {}) | number

  /** Minimal possible value, `0` by default */
  min?: number

  /** Maximum possible value, `100` by default */
  max?: number

  /** Number by which value will be incremented/decremented with thumb drag and arrows, `1` by default */
  step?: number

  /** Number of significant digits after the decimal point */
  precision?: number

  /** Hidden input name, use with uncontrolled component */
  name?: string

  /** Marks displayed on the track */
  marks?: {value: number; label?: React.ReactNode}[]

  /** Function to generate label or any react node to render instead, set to null to disable label */
  label?: React.ReactNode | ((value: number) => React.ReactNode)

  /** Props passed down to the `Transition` component, `{ transition: 'fade', duration: 0 }` by default */
  labelTransitionProps?: TransitionOverride

  /** Determines whether the label should be visible when the slider is not being dragged or hovered, `false` by default */
  labelAlwaysOn?: boolean

  /** Determines whether thumb label should be displayed when the slider is hovered, `true` by default */
  showLabelOnHover?: boolean

  /** Disables slider */
  disabled?: boolean

  /** Thumb `width` and `height`, by default value is computed based on `size` prop */
  thumbSize?: number | string

  /** Props passed down to the hidden input */
  hiddenInputProps?: React.ComponentPropsWithoutRef<'input'>
}

export type SliderFactory<K extends string = string> = Factory<{
  props: SliderProps<K>
  ref: HTMLDivElement
  stylesNames: SliderStylesNames
  vars: SliderCssVariables
}>

const defaultProps = {
  radius: 'xl',
  min: 0,
  max: 100,
  step: 1,
  marks: [],
  label: f => f,
  labelTransitionProps: {transition: 'fade', duration: 0},
  labelAlwaysOn: false,
  showLabelOnHover: true,
  disabled: false
} satisfies Partial<SliderProps>

const varsResolver = createVarsResolver<SliderFactory>((theme, {size, color, thumbSize, radius}) => ({
  root: {
    '--slider-size': getSize(size, 'slider-size'),
    '--slider-color': color ? getThemeColor(color, theme) : undefined,
    '--slider-radius': radius === undefined ? undefined : getRadius(radius),
    '--slider-thumb-size': thumbSize !== undefined ? rem(thumbSize) : 'calc(var(--slider-size) * 2)'
  }
}))

export const Slider: <K extends string = string>(
  props: SliderProps<K>
) => ReturnType<MantineComponent<SliderFactory<K>>> = factory<SliderFactory>((_props, ref) => {
  const props = useProps('Slider', defaultProps, _props)
  const {
    classNames,
    styles,
    value,
    onChange,
    onDragEnd,
    thumbs,
    size,
    min,
    max,
    step = 1,
    precision: _precision,
    defaultValue,
    name,
    marks,
    label,
    labelTransitionProps,
    labelAlwaysOn,
    showLabelOnHover,
    disabled,
    unstyled,
    className,
    style,
    vars,
    hiddenInputProps,
    thumbSize,
    ...others
  } = props

  const getStyles = useStyles<SliderFactory>({
    name: 'Slider',
    props,
    classes,
    classNames,
    className,
    styles,
    style,
    vars,
    varsResolver,
    unstyled
  })

  const thumbRefs = useRef<Partial<Record<string, HTMLDivElement | null>>>({})
  const thumbName = useRef<string | undefined>(undefined)
  const posish = (v: number) => getPosition({value: v, min, max})
  const stepValue = ((max - min) / 100) * step
  const roundedValue = (v: number) => (precision != null ? Number(v.toPrecision(precision)) : v)
  const {ref: moveRef, active: moveActive} = useDrag({
    shouldClamp: false,
    onChange: ({posish: {x}}) => {
      const name = thumbName.current
      if (!name) return
      const dx = x * (max - min)
      const nextValue = dx + min // ignore step value when dragging with mouse
      onChange({name, value: roundedValue(nextValue), mode: 'drag'})
    },
    onEnd: () => {
      onDragEnd?.()
    }
  })

  const thumbProps = (name: string, v: number) => {
    const {showLabel, shouldClamp, onFocus, shape, children, hideOutOfRange, ...passThroughProps} = thumbs[name]!
    const active = moveActive && name == thumbName.current
    const props: ThumbProps & RefProps<HTMLDivElement> = {
      shape: shape ?? 'circle',
      name,
      max,
      min,
      labelAlwaysOn,
      labelTransitionProps,
      disabled,
      value: v,
      position: posish(v),
      dragging: active,
      label: showLabel ? (typeof label === 'function' ? label(v) : label) : null,
      showLabelOnHover,
      children: children?.(v),
      hideOutOfRange,
      ref: node => {
        thumbRefs.current[name] = node
      },
      onMouseDown: _ => {
        thumbRefs.current[name]?.focus()
      },
      onFocus: () => {
        thumbName.current = name
        onFocus?.()
      },
      ...passThroughProps
    }
    return props
  }

  const root = useRef<HTMLDivElement>(null)
  const precision = _precision ?? getPrecision(step!)
  const thumbItems = value && mapEntries(value, (name, v) => thumbProps(name, v))

  return (
    <SliderProvider value={{getStyles}}>
      <SliderRoot
        {...others}
        ref={useMergedRef(ref, root, moveRef)}
        onKeyDown={({key, ctrlKey}) => {
          const name = thumbName.current
          if (!name) return
          const thumb = thumbRefs.current[name]
          if (!thumb) return
          const sv = (ctrlKey ? 0.1 : 1) * stepValue
          match(key)
            .with('ArrowRight', () => onChange({name, value: roundedValue(value[name]! + sv), mode: 'arrows'}))
            .with('ArrowLeft', () => onChange({name, value: roundedValue(value[name]! - sv), mode: 'arrows'}))
            .otherwise(() => {})
        }}
        onMouseDownCapture={() => root.current?.focus()}
        size={size!}
        disabled={disabled}>
        <Track
          offset={0}
          marks={marks}
          min={min!}
          max={max!}
          disabled={disabled}
          containerProps={{
            ref: moveRef as any
          }}>
          {thumbItems?.map(({children, ref, ...props}) => (
            <Thumb key={props.name} ref={ref as any} {...props} showLabelOnHover={true}>
              {children}
            </Thumb>
          ))}
        </Track>
      </SliderRoot>
    </SliderProvider>
  )
})

//@ts-ignore
Slider.classes = classes
//@ts-ignore
Slider.displayName = '@mantine/core/Slider'
